/**
 * useSimpleTableFiltering
 *
 * Applies client-side search, simple filters, and sorting to raw table data.
 * Returns the fully processed (filtered + sorted) dataset.
 *
 * Sorting strategy:
 *   1. FTS active  → sort by _relevance descending
 *   2. Multi-column sort configured → applyMultiColumnSort
 *   3. Single-column sort (legacy fallback) → applySingleColumnSort
 */

import { useMemo } from "react";
import type { BaseEntity, SimpleTableConfig, EntityWithRelations } from "../types";
import { applyMultiColumnSort, applySingleColumnSort } from "../utils";

interface UseSimpleTableFilteringProps<TData extends BaseEntity> {
  config: SimpleTableConfig<TData>;
  data: TData[];
  searchTerm: string;
  simpleFilters: Record<string, any>;
  sortField: string | null;
  sortDirection: "asc" | "desc";
  sortColumns: Array<{ field: string; direction: "asc" | "desc" }>;
  searchableFields: string[];
  isDataPreFiltered: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns true when the dataset carries FTS relevance scores and a search is active. */
function isFTSActive<T extends BaseEntity>(data: T[], searchTerm: string): boolean {
  return (
    data.length > 0 &&
    (data[0] as EntityWithRelations<T>)._relevance !== undefined &&
    searchTerm.trim().length > 0
  );
}

/** Sort by _relevance descending (highest relevance first). */
function sortByRelevance<T extends BaseEntity>(data: T[]): T[] {
  return [...data].sort((a, b) => {
    const ra = (a as EntityWithRelations<T>)._relevance ?? 0;
    const rb = (b as EntityWithRelations<T>)._relevance ?? 0;
    return rb - ra;
  });
}

/** Apply whichever sort strategy is active. */
function applySort<T extends BaseEntity>(
  data: T[],
  sortColumns: Array<{ field: string; direction: "asc" | "desc" }>,
  sortField: string | null,
  sortDirection: "asc" | "desc",
  multiSortComparator: (a: T, b: T) => number
): T[] {
  if (sortColumns.length > 0) {
    return [...data].sort(multiSortComparator);
  }
  if (sortField) {
    return [...data].sort((a, b) =>
      applySingleColumnSort(
        a,
        b,
        sortField,
        sortDirection
      )
    );
  }
  return data;
}

/** Client-side search across searchable fields. */
function applySearch<T extends BaseEntity>(
  data: T[],
  searchTerm: string,
  searchableFields: string[]
): T[] {
  const term = searchTerm.toLowerCase().trim();
  if (!term || searchableFields.length === 0) return data;

  return data.filter((item) => {
    const typed = item as EntityWithRelations<T>;
    return searchableFields.some((fieldId) => {
      const value = typed[fieldId as keyof typeof typed];
      if (value === null || value === undefined) return false;

      if (typeof value === "string") return value.toLowerCase().includes(term);
      if (typeof value === "number") return value.toString().includes(term);

      if (Array.isArray(value)) {
        return value.some((v) => String(v).toLowerCase().includes(term));
      }

      if (typeof value === "object") {
        const obj = value as Record<string, unknown>;
        // Named-person objects
        if (obj.name || obj.first_name || obj.last_name || obj.email) {
          return [obj.name, obj.first_name, obj.last_name, obj.email]
            .filter(Boolean)
            .some((f) => String(f).toLowerCase().includes(term));
        }
        // Title / subject objects
        if (obj.title || obj.subject) {
          return String(obj.title || obj.subject).toLowerCase().includes(term);
        }
        // Description / notes objects
        if (obj.description || obj.notes) {
          return String(obj.description || obj.notes).toLowerCase().includes(term);
        }
        // Arrays of related objects
        if (Array.isArray(obj)) {
          return (obj as unknown[]).some((arrayItem) => {
            if (typeof arrayItem === "object" && arrayItem !== null) {
              const ai = arrayItem as Record<string, unknown>;
              return ["name", "first_name", "last_name", "email", "title", "subject"].some(
                (f) => ai[f] && String(ai[f]).toLowerCase().includes(term)
              );
            }
            return String(arrayItem).toLowerCase().includes(term);
          });
        }
        // Generic object fallback
        return Object.values(obj).some((v) => String(v).toLowerCase().includes(term));
      }

      return String(value).toLowerCase().includes(term);
    });
  });
}

/** Apply simple (equality / array-membership) filters. */
function applySimpleFilters<T extends BaseEntity>(
  data: T[],
  simpleFilters: Record<string, any>
): T[] {
  let filtered = data;

  Object.entries(simpleFilters).forEach(([field, value]) => {
    if (value === undefined || value === null || value === "" || value === "all") return;

    const actualField = field.startsWith("filter_") ? field.replace("filter_", "") : field;

    if (Array.isArray(value)) {
      filtered = filtered.filter(
        (item) => value.includes((item as EntityWithRelations<T>)[actualField as keyof EntityWithRelations<T>])
      );
      return;
    }

    // Special handling for is_approved
    if (actualField === "is_approved") {
      if (value === "disapproved") {
        filtered = filtered.filter(
          (item) => (item as EntityWithRelations<T>).sync_status === "disapproved"
        );
      } else if (typeof value === "boolean" && value === false) {
        filtered = filtered.filter(
          (item) => {
            const typed = item as EntityWithRelations<T>;
            return typed[actualField as keyof typeof typed] === false &&
              typed.sync_status !== "disapproved";
          }
        );
      } else if (typeof value === "boolean" && value === true) {
        filtered = filtered.filter(
          (item) =>
            (item as EntityWithRelations<T>)[actualField as keyof EntityWithRelations<T>] === true
        );
      }
      return;
    }

    if (typeof value === "boolean") {
      filtered = filtered.filter(
        (item) =>
          (item as EntityWithRelations<T>)[actualField as keyof EntityWithRelations<T>] === value
      );
      return;
    }

    const filterValue = String(value);
    filtered = filtered.filter(
      (item) =>
        String(
          (item as EntityWithRelations<T>)[actualField as keyof EntityWithRelations<T>]
        ).toLowerCase() === filterValue.toLowerCase()
    );
  });

  return filtered;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSimpleTableFiltering<TData extends BaseEntity>({
  config,
  data,
  searchTerm,
  simpleFilters,
  sortField,
  sortDirection,
  sortColumns,
  searchableFields,
  isDataPreFiltered,
}: UseSimpleTableFilteringProps<TData>) {
  const multiSortComparator = useMemo(() => {
    // applyMultiColumnSort expects an explicit priority on each entry; the
    // hook's caller already orders columns by priority, so we assign one based
    // on array index.
    const prioritisedSortColumns = sortColumns.map((col, index) => ({
      ...col,
      priority: index,
    }));
    return (a: TData, b: TData) =>
      applyMultiColumnSort(
        a,
        b,
        prioritisedSortColumns
      );
  }, [sortColumns]);

  const processedData = useMemo(() => {
    // -----------------------------------------------------------------------
    // Pre-filtered path (data already filtered server-side / by parent)
    // -----------------------------------------------------------------------
    if (isDataPreFiltered) {
      if (isFTSActive(data, searchTerm)) {
        return sortByRelevance(data);
      }
      return applySort(data, sortColumns, sortField, sortDirection, multiSortComparator);
    }

    // -----------------------------------------------------------------------
    // Client-side path
    // -----------------------------------------------------------------------
    let filtered = data;

    if (searchTerm && config.features?.search) {
      filtered = applySearch(filtered, searchTerm, searchableFields);
    }

    filtered = applySimpleFilters(filtered, simpleFilters);

    // Advanced filters are handled server-side — no client-side step needed.

    if (isFTSActive(filtered, searchTerm)) {
      return sortByRelevance(filtered);
    }

    return applySort(filtered, sortColumns, sortField, sortDirection, multiSortComparator);
  }, [
    data,
    searchTerm,
    simpleFilters,
    sortColumns,
    sortField,
    sortDirection,
    multiSortComparator,
    searchableFields,
    isDataPreFiltered,
    config.features?.search,
  ]);

  return { processedData };
}
